// Basic -> Creator, at the moment a Basic subscriber reaches for something
// only Creator does: training a voice, a 1080p export, or a Marble Run. The generic paywall here showed plan cards for a new customer
// and, after upgrading, sent them to Settings, losing what they were making.
//
// This asks someone who already pays: it shows what the upgrade costs TODAY
// (a proration Stripe computes, from /api/payment/subscription/upgrade-preview
// - never guessed on the client, see PostPurchaseUpsell) and the monthly
// price after, and when they upgrade it refreshes their account and hands
// back to the caller, which carries on with what they were doing.
//
// Callers show it to "basic" and "early adopter" (secret plans) only; free
// users still get UpgradeModal, which starts a subscription.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { getIdToken } from "../../utils/firebaseAuth";
import { Check } from "lucide-react";
import { JamSheet } from "@jammable/ui-components/Dialog";
import { Spinner } from "@jammable/ui-components/Loader";
import { getAuthHeaders } from "@jammable/clientapi/src/shared";
import { stripeAmountToMajor } from "@jammable/clientapi/src/payments";
import { mutationSubscriptionUpgrade } from "../../../../packages/ui/api/src/payments/mutation";
import { getSubscriptionStatus } from "@jammable/ui-payments-feature/src/utils/subscriptions";
import { useAuth } from "../../context/AuthProvider";
import { formatPriceWithLocale } from "../../utils/pricing";
import { track } from "../../utils/analytics";

/* eslint-disable @typescript-eslint/no-explicit-any */

export type CreatorUpsellReason =
  | "train"
  | "video-export"
  | "marble-run"
  | "video";

/** Who sees this sheet rather than the new-subscriber paywall. */
export const canUpsellToCreator = (user: any) => {
  const plan = user?.subscriptions
    ? getSubscriptionStatus(user.subscriptions)
    : "free";
  return plan === "basic" || plan === "early adopter";
};

const COPY: Record<
  CreatorUpsellReason,
  { title: string; lead: string; ticks: string[]; cta: string }
> = {
  train: {
    title: "Start training with Creator",
    lead: "Training a voice comes with Creator.",
    ticks: [
      "Train unlimited voices",
      "Marble Run videos, 50 coins a month",
      "Everything in your plan stays",
    ],
    cta: "Upgrade and start training",
  },
  "video-export": {
    title: "Export in 1080p",
    lead: "Creator exports your videos in full 1080p.",
    ticks: [
      "1080p exports",
      "Marble Run videos, 50 coins a month",
      "Train unlimited voices",
    ],
    cta: "Upgrade and export in 1080p",
  },
  "marble-run": {
    title: "Marble Run is on Creator",
    lead: "Turn covers into Marble Run videos, ready for TikTok and Reels.",
    ticks: [
      "50 Marble Run coins every month",
      "1080p studio exports",
      "Train unlimited voices",
    ],
    cta: "Upgrade and make a Marble Run",
  },
  video: {
    title: "Videos are on Creator",
    lead: "Turn your covers into shareable videos for TikTok and Reels.",
    ticks: [
      "Shareable videos, 50 coins a month",
      "1080p studio exports",
      "Train unlimited voices",
    ],
    cta: "Upgrade and make my video",
  },
};

type Preview = {
  currency: string;
  dueToday: number;
  regularAmount: number;
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
// The default for when a refreshed account counts as upgraded.
const isCreator = (account: any) =>
  getSubscriptionStatus(account?.subscriptions) === "creator";
const formatDuration = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m ? `${m} min${s ? ` ${s} s` : ""}` : `${s} s`;
};

export default function CreatorUpsellSheet({
  open,
  reason,
  onClose,
  onUpgraded,
  alternative,
  samples,
  layer,
  ready = isCreator,
}: {
  open: boolean;
  reason: CreatorUpsellReason;
  /** Closed without upgrading. */
  onClose: () => void;
  /** Upgraded and the account refreshed: carry on with what they were doing. */
  onUpgraded: React.Dispatch<any>;
  /** A way to go on without upgrading, e.g. "Export at 720p with the mark". */
  alternative?: { label: string; onClick: () => void };
  /** Training: what they've uploaded. */
  samples?: { files: number; seconds: number };
  /** When the refreshed account counts as upgraded for this action. */
  ready?: typeof isCreator;
  /** z-index, when opened from inside a ModalContext modal (10000+). */
  layer?: number;
}) {
  const { updateUser } = useAuth();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [priceError, setPriceError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = COPY[reason];

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    track("Creator Upsell Shown", { reason });
    (async () => {
      try {
        const headers = { ...(await getAuthHeaders()) };
        if (cancelled) return;
        // Fresh state for this opening.
        setError(null);
        setPriceError(false);
        const res = await fetch(
          "/api/payment/subscription/upgrade-preview?plan=Jammable%20Creator",
          { headers },
        );
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        if (!cancelled) setPreview(data);
      } catch (e) {
        console.warn("creator upsell: could not price the upgrade", e);
        if (!cancelled) setPriceError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, reason]);

  // The upgrade API records the new plan on the account straight away, so the
  // first read normally has it; a few retries cover a slow write.
  const refreshAccount = async () => {
    let fresh: any = null;
    for (let i = 0; i < 6; i++) {
      try {
        const token = await getIdToken();
        fresh = (
          await axios.get("/api/user", { headers: { Authorization: token } })
        ).data;
        if (ready(fresh)) break;
      } catch {
        // Try again.
      }
      await sleep(1000);
    }
    if (fresh) updateUser(fresh);
    return fresh;
  };

  const upgrade = async () => {
    setBusy(true);
    setError(null);
    track("Creator Upsell Accepted", {
      reason,
      dueToday: preview?.dueToday,
      currency: preview?.currency,
    });
    try {
      await mutationSubscriptionUpgrade(
        "Jammable Creator",
        undefined,
        undefined,
        `creator-sheet:${reason}`,
      );
    } catch (e) {
      console.warn("creator upsell: upgrade failed", e);
      // payment_behavior error_if_incomplete: a declined payment changes
      // nothing, so nothing was charged.
      setError(
        "The payment didn't go through, so nothing changed. Check your card in Settings and try again.",
      );
      setBusy(false);
      return;
    }
    const fresh = await refreshAccount();
    setBusy(false);
    onUpgraded(fresh);
  };

  const close = () => {
    if (busy) return;
    track("Creator Upsell Dismissed", { reason });
    onClose();
  };

  const cur = preview?.currency ?? "usd";
  const money = (amount: number) =>
    formatPriceWithLocale(stripeAmountToMajor(amount, cur), cur);
  const today = preview ? Math.max(0, preview.dueToday) : null;

  return (
    <JamSheet
      open={open}
      onOpenChange={(o) => !o && close()}
      title={copy.title}
      layer={layer}
      footer={
        <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-2">
          {error && (
            <p className="tw-m-0 tw-text-center tw-text-[13px] tw-font-bold tw-text-red-500">
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={upgrade}
            disabled={busy || !preview}
            className="tw-flex tw-min-h-[54px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border-0 tw-bg-jam-lime tw-px-5 tw-text-[16px] tw-font-extrabold tw-text-jam-ink tw-transition-colors hover:tw-bg-[#C4EE00] disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          >
            {busy && (
              <Spinner size={4} className="tw-border-3 tw-border-jam-ink" />
            )}
            {busy ? "Upgrading..." : copy.cta}
          </button>
          {alternative ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                track("Creator Upsell Alternative", { reason });
                alternative.onClick();
              }}
              className="tw-cursor-pointer tw-self-center tw-border-0 tw-bg-transparent tw-p-1 tw-text-[13px] tw-font-bold tw-text-jam-muted tw-underline tw-underline-offset-[3px] hover:tw-text-jam-ink"
            >
              {alternative.label}
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={close}
              className="tw-cursor-pointer tw-self-center tw-border-0 tw-bg-transparent tw-p-1 tw-text-[13px] tw-font-bold tw-text-jam-muted tw-underline tw-underline-offset-[3px] hover:tw-text-jam-ink"
            >
              Not now
            </button>
          )}
        </div>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-jam-muted">
          {samples
            ? `Your vocals are ready: ${samples.files} ${samples.files === 1 ? "file" : "files"}, ${formatDuration(samples.seconds)}. `
            : ""}
          {copy.lead}
        </p>
        <div className="tw-flex tw-flex-col tw-gap-2.5 tw-rounded-[22px] tw-bg-jam-ink tw-p-4 tw-text-white">
          {preview && today !== null ? (
            <>
              <div className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-2 tw-gap-y-1">
                <b className="jam-display tw-text-[40px] tw-leading-none tw-text-jam-lime">
                  {today > 0 ? money(today) : "Nothing"}
                </b>
                <span className="tw-text-[15px] tw-font-extrabold">
                  {today > 0 ? "today" : "to pay today"}
                </span>
                <span className="tw-text-[13px] tw-font-bold tw-text-white/70">
                  then {money(preview.regularAmount)} a month
                </span>
              </div>
              <span className="tw-text-xs tw-font-bold tw-text-white/70">
                The rest of your current month is credited. Cancel any time.
              </span>
            </>
          ) : priceError ? (
            <span className="tw-text-sm tw-font-bold">
              We couldn&apos;t load your upgrade price. You can upgrade any time
              from Settings.
            </span>
          ) : (
            <span className="tw-flex tw-h-10 tw-items-center">
              <Spinner size={4} className="tw-border-3 tw-border-white" />
            </span>
          )}
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
            {copy.ticks.map((t) => (
              <li
                key={t}
                className="tw-flex tw-items-center tw-gap-2.5 tw-text-[13px] tw-font-bold"
              >
                <span className="tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-jam-lime tw-text-jam-ink">
                  <Check size={12} strokeWidth={3.5} />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </JamSheet>
  );
}
